import { useState, useEffect, useCallback } from 'react';
import { OrderLog } from '../../services/orderService';
import apiClient from '../../config/axiosConfig';

interface OrderStats {
  totalOrders: number;
  todayOrders: number;
  pendingOrders: number;
  preparingOrders: number;
  totalRevenue: number;
}

interface UseAdminDashboardReturn {
  // Data
  stats: OrderStats | null;
  recentOrders: OrderLog[];

  // States
  loading: boolean;
  error: string | null;

  // Actions
  refreshData: () => Promise<void>;

  // Utils
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
}

export const useAdminDashboard = (): UseAdminDashboardReturn => {
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [recentOrders, setRecentOrders] = useState<OrderLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // İstatistikleri ve son siparişleri paralel olarak getir
      const [statsResponse, ordersResponse] = await Promise.all([
        apiClient.get('/api/orders/stats'),
        apiClient.get('/api/orders/recent/5'),
      ]);

      setStats(statsResponse.data);
      setRecentOrders(ordersResponse.data);
    } catch (err) {
      console.error('Dashboard veri yükleme hatası:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Dashboard verileri yüklenirken hata oluştu';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sipariş durumu rengini belirle
  const getStatusColor = useCallback((status: string): string => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'preparing':
        return 'bg-blue-100 text-blue-800';
      case 'on-the-way':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  // Yerel status text wrapper fonksiyonu
  const getStatusText = useCallback((status: string): string => {
    switch (status) {
      case 'pending':
        return 'Bekliyor';
      case 'preparing':
        return 'Hazırlanıyor';
      case 'on-the-way':
        return 'Yolda';
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal Edildi';
      default:
        return status;
    }
  }, []);

  // İlk yükleme
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    // Data
    stats,
    recentOrders,

    // States
    loading,
    error,

    // Actions
    refreshData: fetchData,

    // Utils
    getStatusText,
    getStatusColor,
  };
};
